import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Modal, Form, Input, InputNumber, message, Space, Tag, Switch, Radio, Select } from 'antd';
import { PlusOutlined, EditOutlined } from '@ant-design/icons';
import { customEnumApi, CreateCustomEnumTypeRequest, UpdateCustomEnumTypeRequest, CreateCustomEnumValueRequest, UpdateCustomEnumValueRequest } from '../api/custom_enum';
import { CustomEnumType, CustomEnumValue, CustomParamScenario, CustomEnumMode } from '../api/model';

interface CustomEnumComponentsProps {
  scenarioLabels: Record<CustomParamScenario, string>;
  loading: boolean;
}

const CustomEnumComponents: React.FC<CustomEnumComponentsProps> = ({ scenarioLabels, loading }) => {
  const [enumTypes, setEnumTypes] = useState<CustomEnumType[]>([]);
  const [enumValues, setEnumValues] = useState<Record<string, CustomEnumValue[]>>({});
  const [valueModalVisible, setValueModalVisible] = useState(false);
  const [typeModalVisible, setTypeModalVisible] = useState(false);
  const [editingValue, setEditingValue] = useState<CustomEnumValue | null>(null);
  const [editingType, setEditingType] = useState<CustomEnumType | null>(null);
  const [currentTypeId, setCurrentTypeId] = useState<string>('');
  const [valueForm] = Form.useForm();
  const [typeForm] = Form.useForm();

  // 获取所有自定义枚举类型
  const fetchEnumTypes = async () => {
    try {
      const response = await customEnumApi.listCustomEnumTypes();
      if (response.success) {
        // 过滤掉全局类型（is_global为false的才是医生自定义的）
        const nonGlobalTypes = response.data.filter(type => !type.is_global);
        setEnumTypes(nonGlobalTypes);
        
        // 初始化每个类型的枚举值
        for (const type of nonGlobalTypes) {
          await fetchEnumValues(type.id);
        }
      } else {
        message.error('获取枚举类型失败: ' + response.message);
      }
    } catch (error) {
      message.error('获取枚举类型失败');
    }
  };

  // 获取指定类型的枚举值
  const fetchEnumValues = async (typeId: string) => {
    try {
      const response = await customEnumApi.listCustomEnumValues(typeId);
      if (response.success) {
        setEnumValues(prev => ({ ...prev, [typeId]: response.data }));
      } else {
        message.error(`获取枚举值失败: ${response.message}`);
      }
    } catch (error) {
      message.error('获取枚举值失败');
    }
  };

  // 组件加载时获取枚举类型
  useEffect(() => {
    fetchEnumTypes();
  }, []);

  // 处理创建枚举值
  const handleCreateValue = (typeId: string) => {
    setCurrentTypeId(typeId);
    setEditingValue(null);
    valueForm.resetFields();
    setValueModalVisible(true);
  };

  // 处理编辑枚举值
  const handleEditValue = (value: CustomEnumValue) => {
    setEditingValue(value);
    setCurrentTypeId(value.type_id.toString());
    valueForm.setFieldsValue({
      name: value.name,
      description: value.description,
      order: value.order,
      extra: value.extra ? JSON.stringify(value.extra, null, 2) : '',
      is_enabled: value.is_enabled
    });
    setValueModalVisible(true);
  };

  // 处理创建枚举类型
  const handleCreateType = () => {
    setEditingType(null);
    typeForm.resetFields();
    setTypeModalVisible(true);
  };

  // 处理编辑枚举类型
  const handleEditType = (type: CustomEnumType) => {
    setEditingType(type);
    typeForm.setFieldsValue({
      name: type.name,
      scenario: type.scenario,
      order: type.order,
      extra: type.extra ? JSON.stringify(type.extra, null, 2) : '',
      is_enabled: type.is_enabled,
      visible_to_customer: type.visible_to_customer
    });
    setTypeModalVisible(true);
  };

  // 处理枚举值启用状态变更
  const handleValueEnabledChange = async (value: CustomEnumValue, checked: boolean) => {
    try {
      const response = await customEnumApi.setCustomEnumValueEnabled(value.id, checked);
      if (response.success) {
        message.success('更新枚举值启用状态成功');
        // 重新获取该类型的枚举值
        await fetchEnumValues(value.type_id.toString());
      } else {
        message.error('更新枚举值启用状态失败: ' + response.message);
      }
    } catch (error) {
      console.error('更新枚举值启用状态失败:', error);
      message.error('更新枚举值启用状态失败');
    }
  };

  // 处理枚举类型启用状态变更
  const handleTypeEnabledChange = async (type: CustomEnumType, checked: boolean) => {
    try {
      const response = await customEnumApi.setCustomEnumTypeEnabled(type.id, checked);
      if (response.success) {
        message.success('更新枚举类型启用状态成功');
        // 重新获取枚举类型
        await fetchEnumTypes();
      } else {
        message.error('更新枚举类型启用状态失败: ' + response.message);
      }
    } catch (error) {
      console.error('更新枚举类型启用状态失败:', error);
      message.error('更新枚举类型启用状态失败');
    }
  };

  // 保存枚举值
  const handleSaveValue = async () => {
    try {
      const values = await valueForm.validateFields();
      
      // 解析extra字段为JSON对象
      let extraObj: Record<string, any> | undefined;
      if (values.extra) {
        extraObj = JSON.parse(values.extra);
      }
      
      if (editingValue) {
        // 更新枚举值
        const updateRequest: UpdateCustomEnumValueRequest = {
          name: values.name,
          description: values.description,
          order: values.order,
          extra: extraObj
        };
        const response = await customEnumApi.updateCustomEnumValue(editingValue.id, updateRequest);
        
        if (response.success) {
          message.success('更新成功');
          setValueModalVisible(false);
          valueForm.resetFields();
          // 重新获取该类型的枚举值
          await fetchEnumValues(editingValue.type_id.toString());
        } else {
          message.error('更新失败: ' + response.message);
        }
      } else {
        // 创建枚举值
        const createRequest: CreateCustomEnumValueRequest = {
          type_id: currentTypeId,
          name: values.name,
          description: values.description,
          order: values.order,
          extra: extraObj,
          is_enabled: values.is_enabled
        };
        const response = await customEnumApi.createCustomEnumValue(createRequest);
        
        if (response.success) {
          message.success('创建成功');
          setValueModalVisible(false);
          valueForm.resetFields();
          // 重新获取该类型的枚举值
          await fetchEnumValues(currentTypeId);
        } else {
          message.error('创建失败: ' + response.message);
        }
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 保存枚举类型
  const handleSaveType = async () => {
    try {
      const values = await typeForm.validateFields();
      
      // 解析extra字段为JSON对象
      let extraObj: Record<string, any> | undefined;
      if (values.extra) {
        extraObj = JSON.parse(values.extra);
      }
      
      if (editingType) {
        // 更新枚举类型
        const updateRequest: UpdateCustomEnumTypeRequest = {
          name: values.name,
          scenario: values.scenario,
          order: values.order,
          extra: extraObj,
          visible_to_customer: values.visible_to_customer
        };
        const response = await customEnumApi.updateCustomEnumType(editingType.id, updateRequest);
        
        if (response.success) {
          message.success('更新成功');
          setTypeModalVisible(false);
          typeForm.resetFields();
          // 重新获取枚举类型
          await fetchEnumTypes();
        } else {
          message.error('更新失败: ' + response.message);
        }
      } else {
          // 创建枚举类型
          const createRequest: CreateCustomEnumTypeRequest = {
            name: values.name,
            scenario: values.scenario,
            mode: values.mode,
            is_enabled: values.is_enabled,
            order: values.order,
            extra: extraObj,
            visible_to_customer: values.visible_to_customer
          };
          const response = await customEnumApi.createCustomEnumType(createRequest);
        
        if (response.success) {
          message.success('创建成功');
          setTypeModalVisible(false);
          typeForm.resetFields();
          // 重新获取枚举类型
          await fetchEnumTypes();
        } else {
          message.error('创建失败: ' + response.message);
        }
      }
    } catch (error) {
      console.error('保存失败:', error);
      message.error('保存失败');
    }
  };

  // 枚举值表格列定义
  const getValueColumns = (typeId: string) => [
    {
      title: '排序',
      dataIndex: 'order',
      key: 'order',
    },
    {
      title: '名称',
      dataIndex: 'name',
      key: 'name',
    },
    {
      title: '描述',
      dataIndex: 'description',
      key: 'description',
    },
    {
      title: '额外配置',
      dataIndex: 'extra',
      key: 'extra',
      render: (extra: Record<string, any>) => {
        if (!extra) return '-';
        try {
          return <div style={{ maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{JSON.stringify(extra)}</div>;
        } catch (e) {
          return '-';
        }
      },
    },
    {
      title: '状态',
      dataIndex: 'is_enabled',
      key: 'is_enabled',
      render: (is_enabled: boolean, record: CustomEnumValue) => (
        <Switch 
          checked={is_enabled} 
          onChange={(checked) => handleValueEnabledChange(record, checked)}
          checkedChildren="启用" 
          unCheckedChildren="禁用" 
        />
      ),
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: CustomEnumValue) => (
        <Space>
          <Button 
            type="primary" 
            icon={<EditOutlined />} 
            onClick={() => handleEditValue(record)}
            size="small"
          >
            编辑
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 24 }}>
        <h1>参数管理</h1>
        <Button 
          type="primary" 
          icon={<PlusOutlined />} 
          onClick={handleCreateType}
        >
          添加枚举类型
        </Button>
      </div>
      {enumTypes.map(type => (
        <Card 
          key={type.id} 
          title={
          <>
            {type.name}&ensp;
            <Tag color="blue">{scenarioLabels[type.scenario]}</Tag>
            <Tag color={type.mode === CustomEnumMode.Single ? 'green' : 'orange'}>{type.mode === CustomEnumMode.Single ? '单选' : '多选'}</Tag>
            <Tag color="purple">排序: {type.order}</Tag>
            <Tag color={type.visible_to_customer ? 'green' : 'red'}>
              {type.visible_to_customer ? '患者可见' : '患者不可见'}
            </Tag>
            <Switch 
              checked={type.is_enabled} 
              onChange={(checked) => handleTypeEnabledChange(type, checked)}
              checkedChildren="启用" 
              unCheckedChildren="禁用" 
              style={{ marginLeft: 8 }}
            />
          </>
          }
          extra={
            <Space>
              <Button 
                icon={<EditOutlined />} 
                onClick={() => handleEditType(type)}
              >
                编辑类型
              </Button>
              <Button 
                type="primary" 
                icon={<PlusOutlined />} 
                onClick={() => handleCreateValue(type.id)}
              >
                添加枚举值
              </Button>
            </Space>
          }
          style={{ marginBottom: 24 }}
        >
          <Table 
            columns={getValueColumns(type.id)} 
            dataSource={enumValues[type.id] || []} 
            loading={loading}
            pagination={false}
            rowKey="id"
          />
        </Card>
      ))}

      <Modal
        title={editingValue ? "编辑枚举值" : "创建枚举值"}
        open={valueModalVisible}
        onOk={handleSaveValue}
        onCancel={() => {
          setValueModalVisible(false);
          valueForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={valueForm} initialValues={{is_enabled: true, order: 0, extra: ''}} layout="vertical">
          <Form.Item 
            name="name" 
            label="名称" 
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="description" label="描述">
            <Input.TextArea />
          </Form.Item>
          <Form.Item name="order" label="排序" initialValue={0}>
            <InputNumber min={-9999} max={9999} />
          </Form.Item>
          <Form.Item name="extra" label="额外配置(JSON)">
            <Input.TextArea 
              rows={4} 
              placeholder="请输入JSON格式的额外配置"
            />
          </Form.Item>
          {editingValue === null && (
            <Form.Item 
              name="is_enabled" 
              label="是否启用" 
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
        </Form>
      </Modal>

      <Modal
        title={editingType ? "编辑枚举类型" : "创建枚举类型"}
        open={typeModalVisible}
        onOk={handleSaveType}
        onCancel={() => {
          setTypeModalVisible(false);
          typeForm.resetFields();
        }}
        okText="保存"
        cancelText="取消"
      >
        <Form form={typeForm} initialValues={{is_enabled: true, order: 0, extra: ''}} layout="vertical">
          <Form.Item 
            name="name" 
            label="名称" 
            rules={[{ required: true, message: '请输入名称' }]}
          >
            <Input />
          </Form.Item>
          <Form.Item name="scenario" label="场景" rules={[{ required: true, message: '请选择场景' }]}>
            {editingType ? (
              // 编辑模式下，scenario字段只读显示
              <div>
                <Tag key={editingType.scenario} color="blue">
                  {scenarioLabels[editingType.scenario]}
                </Tag>
                <Input type="hidden" value={editingType.scenario} />
              </div>
            ) : (
              // 创建模式下，可以编辑scenario字段
              <Select placeholder="请选择场景">
                {Object.entries(scenarioLabels).map(([scenario, label]) => (
                  <Select.Option key={scenario} value={scenario}>
                    {label}
                  </Select.Option>
                ))}
              </Select>
            )}
          </Form.Item>
          <Form.Item name="mode" label="模式" rules={editingType ? [] : [{ required: true, message: '请选择模式' }]}>  
            {editingType ? (
              // 编辑模式下，mode字段只读显示
              <div>
                <Tag color={editingType.mode === CustomEnumMode.Single ? 'green' : 'orange'}>
                  {editingType.mode === CustomEnumMode.Single ? '单选' : '多选'}
                </Tag>
                <Input type="hidden" value={editingType.mode} />
              </div>
            ) : (
              // 创建模式下，可以编辑mode字段
              <Radio.Group>
                <Radio value={CustomEnumMode.Single}>单选</Radio>
                <Radio value={CustomEnumMode.Multiple}>多选</Radio>
              </Radio.Group>
            )}
          </Form.Item>
          <Form.Item name="order" label="排序" initialValue={0}>
            <InputNumber min={-9999} max={9999} />
          </Form.Item>
          <Form.Item name="extra" label="额外配置(JSON)">
            <Input.TextArea 
              rows={4} 
              placeholder="请输入JSON格式的额外配置"
            />
          </Form.Item>
          {editingType === null && (
            <Form.Item 
              name="is_enabled" 
              label="是否启用" 
              valuePropName="checked"
            >
              <Switch checkedChildren="启用" unCheckedChildren="禁用" />
            </Form.Item>
          )}
          <Form.Item 
            name="visible_to_customer" 
            label="对患者可见" 
            valuePropName="checked"
            initialValue={false}
          >
            <Switch checkedChildren="可见" unCheckedChildren="不可见" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default CustomEnumComponents;